
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { API } from '../../utils/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuthContext } from '../../context/AuthContext';
import { Plus, Minus } from 'lucide-react';
import { PrivacyDataSection } from '../../components/PrivacyDataSection';
import { HelpFaqSection } from '../../components/HelpFaqSection';

export function ParentSettings({ headers, user }) {
  const { t } = useTranslation();
  // ... existing state ...
  const [scanningBinding, setScanningBinding] = useState(false);
  const [showBindWatch, setShowBindWatch] = useState(false);
  const [code, setCode] = useState('');
  const [bindingQR, setBindingQR] = useState('');
  const [boundUsers, setBoundUsers] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [budgetForm, setBudgetForm] = useState({
    max_per_month: 10000,
    max_per_day: 1000,
    max_per_transaction: 500,
    allow_overspend: false,
    budget_type: 'INDIVIDUAL'
  });

  // Categories State
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState({ name: '', icon: '🏷️' });
  const categoryIconOptions = ['🏷️', '🍔', '🚌', '🏠', '🛍️', '🎮', '🎁', '📚', '⚽', '🧾', '💊', '🎵', '✈️'];

  const [globalLimits, setGlobalLimits] = useState({
    max_per_month: 50000,
    max_per_day: 5000,
    max_per_transaction: 2000
  });

  // V3.1 Watch Settings State
  const [watchBindings, setWatchBindings] = useState([]);
  const [watchStatus, setWatchStatus] = useState([]);
  const [watchAliases, setWatchAliases] = useState([]);

  // ... existing functions ...
  const fetchGlobalConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/parent/config`, { headers });
      if (res.data) {
        setGlobalLimits({
          max_per_month: res.data.global_max_per_month || 50000,
          max_per_day: res.data.global_max_per_day || 5000,
          max_per_transaction: res.data.global_max_per_transaction || 2000
        });
      }
    } catch (e) {
      console.error('Failed to fetch global config', e);
    }
  }, [headers]);

  const fetchBoundUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/parent/bound-mobpays`, { headers });
      setBoundUsers(res.data.items || []);
    } catch (e) {
      console.error('Failed to fetch bound users', e);
      toast.error(t('errors.failedToFetch'));
    }
  }, [headers]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/parent/categories`, { headers });
      setCategories(res.data.items || []);
    } catch (e) {
      console.error('Failed to fetch categories', e);
      toast.error(t('errors.failedToFetch'));
    }
  }, [headers]);

  const genCode = async () => {
    try {
      const res = await axios.post(`${API}/parent/generate-binding-code`, {}, { headers });
      setCode(res.data.code);
      
      const QRCode = require('qrcode');
      const qrPayload = { type: 'bind_payer', parent_id: user.id, payer_id: user.id, code: res.data.code };
      const qr = await QRCode.toDataURL(JSON.stringify(qrPayload));
      setBindingQR(qr);
      toast.success('Binding code generated');
    } catch (e) {
      console.error('Failed to generate binding code', e);
      toast.error('Failed to generate binding code');
    }
  };

  const addCategory = async () => {
    if (!newCat.name.trim()) {
      toast.error(t('settings.categoryNameRequired'));
      return;
    }
    try {
      await axios.post(`${API}/parent/categories`, newCat, { headers });
      setNewCat({ name: '', icon: '🏷️' });
      fetchCategories();
      toast.success(t('settings.categoryAdded'));
    } catch (e) {
      console.error('Failed to add category', e);
      toast.error('Failed to add category');
    }
  };

  const deleteCategory = async (id) => {
    try {
      await axios.delete(`${API}/parent/categories/${id}`, { headers });
      fetchCategories();
      toast.success(t('settings.categoryDeleted'));
    } catch (e) {
      console.error('Failed to delete category', e);
      toast.error(t('errors.failedToProcess'));
    }
  };

  const toggleBinding = async (bindingId, enabled) => {
    try {
      await axios.post(`${API}/parent/toggle-binding/${bindingId}?enabled=${enabled}`, {}, { headers });
      fetchBoundUsers();
      toast.success(enabled ? 'User enabled' : 'User disabled');
    } catch (e) {
      console.error('Failed to toggle binding', e);
      toast.error('Failed to update user status');
    }
  };

  const expandUser = (userId) => {
    const user = boundUsers.find(u => u.id === userId);
    if (user && user.budget_params) {
      setBudgetForm({
        max_per_month: user.budget_params.max_per_month || 10000,
        max_per_day: user.budget_params.max_per_day || 1000,
        max_per_transaction: user.budget_params.max_per_transaction || 500,
        allow_overspend: user.budget_params.allow_overspend || false,
        budget_type: user.budget_params.budget_type || 'INDIVIDUAL'
      });
    }
    setExpandedUser(userId);
  };

  const saveBudget = async (userId) => {
    try {
      await axios.post(`${API}/parent/update-budget/${userId}`, budgetForm, { headers });
      fetchBoundUsers();
      toast.success('Budget saved');
    } catch (e) {
      console.error('Failed to save budget', e);
      toast.error('Failed to save budget');
    }
  };

  const deleteBound = async (userId) => {
    try {
      await axios.delete(`${API}/parent/unbind/${userId}`, { headers });
      fetchBoundUsers();
      setDeleteConfirm(null);
      toast.success('User unbound');
    } catch (e) {
      console.error('Failed to delete bound user', e);
      toast.error('Failed to unbind user');
    }
  };

  // V3.1: Fetch watch bindings and settings
  const fetchWatchBindings = useCallback(async () => {
    try {
      const [bindingsRes, statusRes, aliasRes] = await Promise.all([
        axios.get(`${API}/parent/watch-bindings`, { headers }),
        axios.get(`${API}/parent/watch-status`, { headers }),
        axios.get(`${API}/parent/watch-aliases`, { headers })
      ]);

      const bindings = bindingsRes.data.bindings || [];
      const statusItems = statusRes.data.items || [];
      const aliasItems = aliasRes.data.items || [];

      const statusMap = statusItems.reduce((acc, item) => {
        acc[item.child_id] = item;
        return acc;
      }, {});

      const aliasMap = aliasItems.reduce((acc, item) => {
        acc[item.child_id] = item;
        return acc;
      }, {});

      const merged = bindings.map((binding) => ({
        ...binding,
        status: statusMap[binding.child_id],
        alias: aliasMap[binding.child_id]
      }));

      setWatchBindings(merged);
      setWatchStatus(statusItems);
      setWatchAliases(aliasItems);
    } catch (e) {
      console.error('Failed to fetch watch bindings', e);
    }
  }, [headers]);

  // V3.1: Update watch settings
  const updateWatchSettings = async (bindingId, settings) => {
    try {
      await axios.put(`${API}/parent/watch-settings/${bindingId}`, settings, { headers });
      toast.success('Watch settings updated');
      fetchWatchBindings();
    } catch (e) {
      console.error('Failed to update watch settings', e);
      toast.error('Failed to update watch settings');
    }
  };

  const createWatchAlias = async (childId) => {
    try {
      await axios.post(`${API}/parent/watch-alias/${childId}`, {}, { headers });
      toast.success('Watch alias created');
      fetchWatchBindings();
    } catch (e) {
      console.error('Failed to create watch alias', e);
      toast.error(e.response?.data?.detail || 'Failed to create watch alias');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchBoundUsers(), fetchCategories(), fetchGlobalConfig(), fetchWatchBindings()]);
    };
    loadData();
  }, [fetchBoundUsers, fetchCategories, fetchGlobalConfig, fetchWatchBindings]);

  return (
    <div className="space-y-8" data-testid="parent-settings">
      {/* Categories Section */}
      <div>
        <h2 className="text-xl font-semibold mb-4" data-testid="payer-categories-title">{t('modules.categories')}</h2>
        <Card data-testid="payer-categories-card">
          <CardHeader>
            <CardTitle className="text-base">{t('settings.manageCategories')}</CardTitle>
            <CardDescription>{t('settings.categoriesDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input 
                placeholder={t('settings.categoryName')}
                value={newCat.name} 
                onChange={e => setNewCat({...newCat, name: e.target.value})} 
                data-testid="category-name-input"
              />
              <Input 
                className="w-20" 
                placeholder="Icon" 
                value={newCat.icon} 
                onChange={e => setNewCat({...newCat, icon: e.target.value})} 
                data-testid="category-icon-input"
              />
              <Button onClick={addCategory} data-testid="add-category-button">{t('common.add')}</Button>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="category-icon-picker">
              {categoryIconOptions.map((icon, idx) => (
                <button
                  key={`${icon}-${idx}`}
                  type="button"
                  onClick={() => setNewCat({ ...newCat, icon })}
                  className={`h-9 w-9 rounded-full border flex items-center justify-center ${
                    newCat.icon === icon ? 'border-teal-600 bg-teal-50' : 'border-gray-200'
                  }`}
                  data-testid={`category-icon-${idx}`}
                >
                  <span className="text-lg">{icon}</span>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {categories.map(c => (
                <div key={c.id} className="flex items-center justify-between p-2 border rounded bg-gray-50">
                  <span>{c.icon} {c.name}</span>
                  <Button variant="ghost" size="sm" onClick={() => deleteCategory(c.id)} data-testid={`delete-category-${c.id}`}>🗑️</Button>
                </div>
              ))}
              {categories.length === 0 && <div className="text-sm text-muted-foreground">No categories defined.</div>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Managed Users Section - V5.0 */}
      <ManagedUsersSettings headers={headers} />

      {/* Privacy & Data (GDPR) */}
      <PrivacyDataSection headers={headers} user={user} />

      {/* Help & FAQ */}
      <HelpFaqSection headers={headers} />
    </div>
  );
}

function BudgetStepper({ label, value, step, min = 0, max = 50000, onChange, testId }) {
  return (
    <div className="flex items-center justify-between" data-testid={testId}>
      <Label className="text-sm">{label}</Label>
      <div className="flex items-center gap-1.5">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onChange(Math.max(min, value - step))}
          disabled={value <= min}
          data-testid={`${testId}-minus`}
        >
          <Minus className="h-3 w-3" />
        </Button>
        <span className="text-sm font-medium w-28 text-center tabular-nums">
          SEK {value.toLocaleString()}
        </span>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onChange(Math.min(max, value + step))}
          disabled={value >= max}
          data-testid={`${testId}-plus`}
        >
          <Plus className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function ManagedUserAvatar({ mu }) {
  const icon = mu.user_info?.profile_icon;
  if (icon && (icon.startsWith('http://') || icon.startsWith('https://'))) {
    return <img src={icon} alt="" className="h-10 w-10 rounded-full object-cover shrink-0" />;
  }
  const letter = mu.nickname?.charAt(0) || mu.user_info?.full_name?.charAt(0) || '?';
  return (
    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium shrink-0">
      {letter}
    </div>
  );
}

function ManagedUsersSettings({ headers }) {
  const { t } = useTranslation();
  const [managed, setManaged] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [nickname, setNickname] = useState('');
  const [editBudget, setEditBudget] = useState(null);
  const [budget, setBudget] = useState({
    max_per_month: 10000,
    max_per_day: 1000,
    max_per_transaction: 500,
    allow_overspend: false,
    budget_type: 'INDIVIDUAL',
  });

  const fetchManaged = async () => {
    try {
      const res = await axios.get(`${API}/managed-users`, { headers });
      setManaged(res.data.items || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchManaged(); }, [headers]);

  const handleAdd = async () => {
    if (!identifier.trim()) return toast.error(t('managed.swishOrPaymigRequired'));
    try {
      await axios.post(`${API}/managed-users/add`, { identifier, nickname }, { headers });
      toast.success(t('managed.added'));
      setAddOpen(false);
      setIdentifier('');
      setNickname('');
      fetchManaged();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('managed.failedToAdd'));
    }
  };

  const handleRemove = async (id) => {
    try {
      await axios.delete(`${API}/managed-users/${id}`, { headers });
      toast.success(t('managed.removed'));
      fetchManaged();
    } catch (e) { toast.error(t('managed.failedToRemove')); }
  };

  const handleToggle = async (id, enabled) => {
    try {
      await axios.put(`${API}/managed-users/${id}/toggle?enabled=${enabled}`, {}, { headers });
      fetchManaged();
    } catch (e) { toast.error(t('managed.failedToToggle')); }
  };

  const openBudgetEditor = (mu) => {
    const b = mu.budget || {};
    setBudget({
      max_per_month: b.max_per_month ?? 10000,
      max_per_day: b.max_per_day ?? 1000,
      max_per_transaction: b.max_per_transaction ?? 500,
      allow_overspend: b.allow_overspend ?? false,
      budget_type: b.budget_type ?? 'INDIVIDUAL',
    });
    setEditBudget(mu.id);
  };

  const saveBudget = async () => {
    if (!editBudget) return;
    try {
      await axios.put(`${API}/managed-users/${editBudget}/budget`, budget, { headers });
      toast.success(t('managed.budgetSaved'));
      setEditBudget(null);
      fetchManaged();
    } catch (e) { toast.error(t('managed.failedToSaveBudget')); }
  };

  return (
    <>
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold" data-testid="managed-users-title">{t('managed.title')}</h2>
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-managed-user-btn">{t('managed.addUser')}</Button>
        </div>
        {managed.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-managed-users">{t('managed.noUsersYet')}</p>}
        <div className="space-y-3">
          {managed.map(mu => (
            <Card key={mu.id} data-testid={`managed-settings-${mu.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ManagedUserAvatar mu={mu} />
                    <div>
                      <div className="font-medium">{mu.nickname}</div>
                      {mu.status !== 'ACCEPTED' && (
                        <div className="text-xs text-muted-foreground">
                          <Badge variant="secondary" className="text-xs">{mu.status}</Badge>
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {t('managed.spent')}: {mu.spending?.month_total || 0} / {mu.spending?.budget_limit || 0} SEK ({mu.spending?.progress_pct || 0}%)
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={mu.enabled}
                      onCheckedChange={(c) => handleToggle(mu.id, c)}
                      data-testid={`toggle-managed-${mu.id}`}
                    />
                    <Button variant="ghost" size="sm" onClick={() => openBudgetEditor(mu)} data-testid={`edit-budget-${mu.id}`}>Budget</Button>
                    <Button variant="destructive" size="sm" onClick={() => handleRemove(mu.id)} data-testid={`remove-managed-${mu.id}`}>Remove</Button>
                  </div>
                </div>

                {/* Budget Editor — Stepper Controls */}
                {editBudget === mu.id && (
                  <div className="border-t pt-3 space-y-4">
                    <BudgetStepper
                      label="Max per Month"
                      value={budget.max_per_month}
                      step={500}
                      max={50000}
                      onChange={(v) => setBudget(b => ({ ...b, max_per_month: v }))}
                      testId="stepper-month"
                    />
                    <BudgetStepper
                      label="Max per Day"
                      value={budget.max_per_day}
                      step={100}
                      max={10000}
                      onChange={(v) => setBudget(b => ({ ...b, max_per_day: v }))}
                      testId="stepper-day"
                    />
                    <BudgetStepper
                      label="Max per Transaction"
                      value={budget.max_per_transaction}
                      step={50}
                      max={5000}
                      onChange={(v) => setBudget(b => ({ ...b, max_per_transaction: v }))}
                      testId="stepper-transaction"
                    />

                    {/* Allow Overspending Toggle */}
                    <div className="flex items-center justify-between pt-2 border-t" data-testid="allow-overspend-row">
                      <div>
                        <Label className="text-sm">Allow over spending</Label>
                        <p className="text-xs text-muted-foreground">Payments continue even after budget is exceeded</p>
                      </div>
                      <Switch
                        checked={budget.allow_overspend}
                        onCheckedChange={(c) => setBudget(b => ({ ...b, allow_overspend: c }))}
                        data-testid="allow-overspend-toggle"
                      />
                    </div>

                    {/* Global Balance Toggle */}
                    <div className="flex items-center justify-between" data-testid="global-balance-row">
                      <div>
                        <Label className="text-sm">Global balance</Label>
                        <p className="text-xs text-muted-foreground">All managers see consolidated spending totals</p>
                      </div>
                      <Switch
                        checked={budget.budget_type === 'GLOBAL'}
                        onCheckedChange={(c) => setBudget(b => ({ ...b, budget_type: c ? 'GLOBAL' : 'INDIVIDUAL' }))}
                        data-testid="global-balance-toggle"
                      />
                    </div>

                    <Button onClick={saveBudget} className="w-full" data-testid={`save-managed-budget-${mu.id}`}>Save Budget</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Add Managed User Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Managed User</DialogTitle>
            <CardDescription>Enter their Swish number or PayMig ID</CardDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Swish / PayMig ID *</Label>
              <Input value={identifier} onChange={e => setIdentifier(e.target.value)} placeholder="e.g., 0701234567" data-testid="managed-user-identifier" />
            </div>
            <div>
              <Label>Nickname (optional)</Label>
              <Input value={nickname} onChange={e => setNickname(e.target.value)} placeholder="Display name" data-testid="managed-user-nickname" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} data-testid="submit-managed-user">Add User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
